import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { toOptionIdentity, toOptionLabel, useGroupedOptions } from './autocompleteHelpers';

/**
 * The MUI Autocomplete behind every react-widgets Combobox: free text plus a list you can pick
 * from. Extracted from `renderComboBox` so the three call sites that used the widget directly —
 * two react-final-form panes and the offer-actions list — get the same behaviour without
 * inheriting that renderer's own FieldError and spacing, which they each supply themselves.
 *
 * `onChange` is called the way Combobox called it: with the typed string while typing, and with
 * the data item on selection. Callers tell the two apart with `typeof value === 'object'`, so a
 * cleared field reports '' — MUI clears to null, and `typeof null === 'object'`.
 */
/**
 * @param {{
 *   id?: string,
 *   name?: string,
 *   options?: any[],
 *   value?: any,
 *   onChange?: (selected: any) => void,
 *   onSelect?: (selected: any) => void,
 *   onBlur?: () => void,
 *   textField?: string | ((option: any) => any),
 *   valueField?: string,
 *   placeholder?: string,
 *   disabled?: boolean,
 *   busy?: boolean,
 *   error?: boolean,
 *   groupBy?: string | ((option: any) => any),
 *   renderListGroup?: (params: { group: any }) => React.ReactNode,
 *   sx?: object,
 * }} props
 */
export default function ComboBoxAutocomplete({
  id,
  name,
  options,
  value,
  onChange,
  onSelect,
  onBlur,
  textField,
  valueField,
  placeholder,
  disabled,
  busy = false,
  error = false,
  groupBy,
  renderListGroup,
  sx,
}) {
  const getLabel = toOptionLabel(textField);
  const grouping = useGroupedOptions(options, groupBy, renderListGroup);
  const current = value === '' ? null : value;

  return (
    <Autocomplete
      freeSolo
      forcePopupIcon
      autoHighlight
      id={id}
      disabled={disabled}
      {...grouping}
      value={current}
      getOptionLabel={getLabel}
      {...toOptionIdentity(valueField)}
      loading={busy}
      sx={sx}
      onChange={(_event, selected) => {
        onChange(selected === null ? '' : selected);
        // Selection only, never a keystroke — which is where `onInputChange` below reports.
        onSelect?.(selected);
      }}
      onInputChange={(_event, text, reason) => {
        if (reason !== 'input') return;
        onChange(text);
      }}
      onBlur={onBlur}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          placeholder={placeholder}
          error={error}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {busy && <CircularProgress size={16} sx={{ mr: 1 }} />}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
